import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useEffect, useMemo } from 'react';

import { Feature } from '../py-generated/datalouder/feature_flag/types';
import { miscService } from '../services';
import { useGeneralErrorHandler } from './useGeneralErrorHandler';
import { useUser } from './users';

const BETA_DEFAULT_ON_FEATURES: Feature[] = [
  // you can put features here to always enable for beta
  'SHIFT_SCROLL_TO_ZOOM',
];

const makeLocalStorageKey = (userId?: number): string =>
  userId ? `cache:features:${userId}` : 'cache:features:anonymous';

const loadCachedFeatures = (userId?: number): Feature[] => {
  try {
    const cached = JSON.parse(
      localStorage.getItem(makeLocalStorageKey(userId)) || '[]'
    );
    if (Array.isArray(cached)) {
      return cached;
    }
    return [];
  } catch {
    return [];
  }
};

export const useFeatureFlags = (): Feature[] => {
  const user = useUser();

  const { data, error } = useQuery(['getFeatureFlags', user?.id], () =>
    miscService.getFeatureFlags(user?.id)
  );
  useGeneralErrorHandler(error);

  useEffect(() => {
    if (data) {
      localStorage.setItem(makeLocalStorageKey(user?.id), JSON.stringify(data));
    }
  }, [data, user?.id]);

  return useMemo(() => {
    const features = data || loadCachedFeatures(user?.id);
    if (
      ['beta.datalouder.com', 'localhost'].includes(window.location.hostname)
    ) {
      return [...features, ...BETA_DEFAULT_ON_FEATURES];
    }
    return [...features];
  }, [data, user?.id]);
};

export interface WithFeatures {
  features: Feature[];
}

/**
 * HOC for legacy class component support
 */
export function withFeatures<P>(
  WrappedComponent: React.ComponentType<P & WithFeatures>
): React.ComponentType<P> {
  function WithFeatures(props: P) {
    return <WrappedComponent features={useFeatureFlags()} {...props} />;
  }

  return WithFeatures;
}
